/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext, useState } from 'react';

import { useRetracked } from 'js/lib/retracked';

import { Button, Hidden, InlineNotification, breakpoints } from '@coursera/cds-core';
import { LinkIcon } from '@coursera/cds-icons';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { InviteToApplyContext } from 'bundles/account-profile/components/recruiting-profile-viewer/InviteToApplyProvider';
import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import InviteToApplyButton from 'bundles/account-profile/components/recruiting-profile-viewer/components/profile-and-invitation/InviteToApplyButton';
import ShareProfileLinkDialog from 'bundles/account-profile/components/recruiting-profile-viewer/components/profile-and-invitation/ShareProfileLinkDialog';
import { EVENTS } from 'bundles/account-profile/components/recruiting-profile-viewer/eventing/events';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/account-profile';

const styles = {
  btnWrapper: () => css`
    ${breakpoints.up('xs')} {
      margin-top: var(--cds-spacing-200);
    }

    ${breakpoints.down('xs')} {
      position: fixed;
      background: var(--cds-color-blue-50);
      border-top: 1px solid var(--cds-color-grey-50);
      width: 100%;
      bottom: 0;
      left: 0;
      padding: var(--cds-spacing-200);
      z-index: 1;
    }
  `,
  errorMessage: () => css`
    margin-top: var(--cds-spacing-200);
  `,
};

const ProfileButton = () => {
  const [showShareProfileLinkDialog, setShowShareProfileLinkDialog] = useState<boolean>(false);
  const [showFailedToCopyLinkError, setShowFailedToCopyLinkError] = useState<boolean>(false);
  const { editable, isRecruiter, sharedEventingV3Data } = useContext(ProfileContext);
  const { setShowInviteToApplyModal } = useContext(InviteToApplyContext);
  const retrackedTrack = useRetracked();
  const track = useTracker();

  const visibilityTrackingRef: React.MutableRefObject<HTMLButtonElement | null> = useVisibilityTracker('view_button', {
    button: {
      name: EVENTS.shareProfile,
    },
    ...sharedEventingV3Data,
  });

  const onShareFailure = () => setShowFailedToCopyLinkError(true);

  const handleShareProfileLink = () => {
    retrackedTrack({
      trackingData: {},
      trackingName: EVENTS.shareProfile,
      action: 'click',
    });

    track('click_button', {
      button: {
        name: EVENTS.shareProfile,
      },
      ...sharedEventingV3Data,
    });

    setShowFailedToCopyLinkError(false);
    setShowShareProfileLinkDialog(true);
  };

  return (
    <>
      {showShareProfileLinkDialog && (
        <ShareProfileLinkDialog
          isOpen={showShareProfileLinkDialog}
          onClose={() => setShowShareProfileLinkDialog(false)}
          onShareFailure={onShareFailure}
        />
      )}
      {isRecruiter ? (
        <div css={styles.btnWrapper}>
          <Hidden xsDown>
            <InviteToApplyButton
              onClick={() => setShowInviteToApplyModal(true)}
              variant="secondary"
              trackingName={EVENTS.inviteToApply}
            />
          </Hidden>
          <Hidden smUp>
            <InviteToApplyButton
              onClick={() => setShowInviteToApplyModal(true)}
              fullWidth={true}
              trackingName={EVENTS.inviteToApply}
            />
          </Hidden>
        </div>
      ) : (
        <>
          {editable && (
            <>
              <Hidden xsDown>
                {/* I wrap the button with a TrackedDiv to support retracked visibility tracking since I cannot use tracked components on
        CDS buttons. It breaks eventing V3's visibility tracking, which relies on an HTML ref. Using component=
        {TrackedLink2} on a CDS link causes the ref to refer to the TrackedLink component itself, *not* the
        underlying HTML element, which in turn results in the IntersectionObserver used in `useVisibilityTracker()` to 
        error out since we're not referencing an html element. */}
                <TrackedDiv
                  trackingName={EVENTS.shareProfile}
                  requireFullyVisible={false}
                  withVisibilityTracking={true}
                  trackClicks={false}
                  css={styles.btnWrapper}
                >
                  <Button
                    ref={visibilityTrackingRef}
                    variant="secondary"
                    icon={<LinkIcon />}
                    iconPosition="before"
                    onClick={handleShareProfileLink}
                  >
                    {_t('Share profile link')}
                  </Button>
                </TrackedDiv>
              </Hidden>
              {showFailedToCopyLinkError && (
                <InlineNotification severity="error" css={styles.errorMessage}>
                  {_t('Unable to copy link to clipboard. Please try again.')}
                </InlineNotification>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

export default ProfileButton;
